import {
  Component,
  Input,
  signal,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  SidebarConfigService,
  PermissionService,
} from '../../services/sidebar-config.service';
import { MenuItem, MenuSection } from '../../models/types/sidebar-types';
import { NgClass, CommonModule } from '@angular/common';
import { IconComponent } from '../icons/icons.component';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { AssetsCategory } from '../../models/types/assets-category.enum';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'lib-sidebar',
  standalone: true,
  imports: [
    NgClass,
    CommonModule,
    IconComponent,
    TooltipComponent,
    RouterModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  isExpanded = signal(true);
  menuSections: MenuSection[] = [];
  AssetsCategory = AssetsCategory;
  @Input() displayName = '';
  @Input() permissionService?: PermissionService;
  /** Theme suffix appended to icon names, e.g. 'ignyte' will make icons 'SidebarLogo-ignyte' */
  @Input() theme = '';
  /** Custom toggle icon name to be passed from parent. If not provided, uses default icons */
  @Input() toggleIcon?: string;
  @Output() isExpandedChange = new EventEmitter<boolean>();
  @Output() signOut = new EventEmitter<void>();

  private permissionCheckSubscription?: Subscription;
  private mobileQuery = window.matchMedia('(max-width: 767px)');
  private queryListener!: (e: MediaQueryListEvent) => void;

  constructor(
    private configService: SidebarConfigService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Set up media query listener
    this.queryListener = (e: MediaQueryListEvent) => {
      this.handleMediaChange(e.matches);
    };
    this.mobileQuery.addEventListener('change', this.queryListener);

    // Initial check
    this.handleMediaChange(this.mobileQuery.matches);

    this.loadMenuSections();
  }

  private handleMediaChange(isMobile: boolean): void {
    if (isMobile) {
      if (this.isExpanded()) {
        this.isExpanded.set(false);
        this.isExpandedChange.emit(false);
      }
    } else {
      if (!this.isExpanded()) {
        this.isExpanded.set(true);
        this.isExpandedChange.emit(true);
      }
    }
  }

  /**
   * Sign out the current user
   */
  handleSignOut(): void {
    this.signOut.emit();
  }

  /**
   * Theme based icon resolver
   */
  getIconName(icon?: string | null): string {
    if (!icon) return '';
    return this.theme ? `${icon}-${this.theme}` : icon;
  }

  ngOnDestroy(): void {
    if (this.mobileQuery && this.queryListener) {
      this.mobileQuery.removeEventListener('change', this.queryListener);
    }
    if (this.permissionCheckSubscription) {
      this.permissionCheckSubscription.unsubscribe();
    }
  }

  /**
   * Loads menu sections with permission filtering
   */
  private loadMenuSections(): void {
    this.menuSections = this.configService.getMenuSections();
  }

  navigateToRoute(route: string): void {
    this.router.navigate([route]);
  }

  toggleSidebar(): void {
    this.isExpanded.update((v: boolean) => !v);
    this.isExpandedChange.emit(this.isExpanded());
  }

  /**
   * Toggles the expansion state of a menu item with children
   * @param item - Menu item to toggle
   */
  toggleMenuItem(item: MenuItem, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (item.children && item.children.length > 0) {
      item.isExpanded = !item.isExpanded;
    }
  }

  /**
   * Checks if a menu item has children
   * @param item - Menu item to check
   * @returns True if item has children
   */
  hasChildren(item: MenuItem): boolean {
    return !!(item.children && item.children.length > 0);
  }

  /**
   * Handles navigation for menu items
   * If item has children, toggle expansion. If item also has a route, navigate to it.
   * @param item - Menu item clicked
   * @param event - Click event
   */
  handleItemClick(item: MenuItem, event?: Event): void {
    if (this.hasChildren(item)) {
      this.toggleMenuItem(item, event);
      if (item.route) {
        this.navigateToRoute(item.route);
      }
    } else if (item.route) {
      this.navigateToRoute(item.route);
    }
  }

  getInitials(name: string): string {
    if (!name?.trim()) return '';
    const words = name.trim().split(/\s+/);
    return `${words[0]?.[0] ?? ''}${
      words.length > 1 ? words[words.length - 1]?.[0] : ''
    }`.toUpperCase();
  }

  sectionTrackBy(_index: number, section: MenuSection) {
    return section.heading;
  }

  itemTrackBy(_index: number, item: MenuItem) {
    return item.route;
  }
}
